import { useState } from 'react'
import { useAuth } from '../../hooks/useAuth'
import { validatePassword } from '../../services/passwordPolicy'

/**
 * SOVRANOVA — set a new password.
 * (Pre-beta user-journey audit, 4 Sep 2026 — B2.)
 *
 * Shown when the app was opened from a password-reset link. The link has
 * already established a session; this screen spends it on one thing and then
 * hands the person back to their account.
 *
 * It is rendered by the app shell BEFORE the onboarding gate, so someone who
 * resets their password is never dropped into the sign-up questionnaire.
 */
export function ResetPasswordView() {
  const { updatePassword, finishPasswordRecovery, cancelPasswordRecovery } = useAuth()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState(false)

  const submit = async () => {
    if (busy) return
    const problem = validatePassword(password)
    if (problem) { setError(problem); return }
    if (password !== confirm) { setError('Those two passwords don\'t match.'); return }
    setError(null)
    setBusy(true)
    try {
      await updatePassword(password)
      setDone(true)
    } catch (err) {
      const msg = String(err?.message || '')
      // The recovery session expires like any other link. Say so plainly and
      // send them back to ask for a fresh one rather than leaving them stuck.
      setError(/expired|invalid|session/i.test(msg)
        ? 'This reset link has expired. Please go back and request a new one.'
        : 'We couldn\'t change your password just then. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="auth-screen">
        <div className="auth-card">
          <div className="auth-brand">◎ SOVRANOVA</div>
          <p style={{ fontWeight: 600, fontSize: '1.02rem', marginTop: '0.6rem' }}>Your password is updated</p>
          <p style={{ fontSize: '0.86rem', color: 'var(--og-n700, #645c50)', lineHeight: 1.6, marginTop: '0.5rem' }}>
            You're signed in and ready to go. Next time, use your new password.
          </p>
          <button className="auth-submit" style={{ marginTop: '1.1rem' }} onClick={finishPasswordRecovery}>
            Continue
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="auth-screen">
      <div className="auth-card">
        <div className="auth-brand">◎ SOVRANOVA</div>
        <p className="auth-tagline">Choose a new password.</p>
        <div className="auth-fields">
          <input type="password" placeholder="New password" value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            autoComplete="new-password" />
          <input type="password" placeholder="Confirm new password" value={confirm}
            onChange={e => setConfirm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && submit()}
            autoComplete="new-password" />
        </div>
        {error && <p className="auth-error">{error}</p>}
        <button className="auth-submit" onClick={submit} disabled={busy || !password || !confirm}>
          {busy ? 'Saving…' : 'Save new password'}
        </button>
        <button type="button" className="auth-linkbtn" onClick={() => cancelPasswordRecovery()}>
          Cancel and sign in instead
        </button>
      </div>
    </div>
  )
}
